import PropTypes from 'prop-types';
import { React, useRef, useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { Avatar, Box, ButtonBase, CardContent, ClickAwayListener, Grid, Paper, Popper, Stack, Typography } from '@mui/material';
import MainCard from '../../../../../../view-components/MainCard';
import Transitions from '../../../../../../view-components/@extended/Transitions';
import ProfileTab from './ProfileTab';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { PersonOutline } from '@mui/icons-material';
import { logger } from '../../../../../../utils/logger';

// tab panel wrapper
function TabPanel({ children, value, index, ...other }) {
    return (
        <div role="tabpanel" hidden={value !== index} id={`profile-tabpanel-${index}`} aria-labelledby={`profile-tab-${index}`} {...other}>
            {value === index && children}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.any.isRequired,
    value: PropTypes.any.isRequired
};

const Profile = () => {
    const theme = useTheme();
    const { user } = useAuthenticator((context) => [context.user]);
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [userGroup, setUserGroup] = useState('');

    useEffect(() => {
        const getUserInfo = async () => {
            try {
                const session = await fetchAuthSession();
                const payload = session.tokens?.idToken?.payload;
                if (payload) {
                    setUserEmail(payload.email || '');
                    const groups = payload['cognito:groups'];
                    setUserGroup(Array.isArray(groups) && groups.length > 0 ? groups[0] : '');
                }
            } catch (e) {
                logger.log('Error fetching user info:', e);
            }
        };
        if (user) {
            getUserInfo();
        }

        // Listen for email change events from Account page
        const handleEmailChanged = () => {
            getUserInfo();
        };
        window.addEventListener('userEmailChanged', handleEmailChanged);
        return () => {
            window.removeEventListener('userEmailChanged', handleEmailChanged);
        };
    }, [user]);

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const [value] = useState(0);
    const isDarkMode = theme.palette.mode === 'dark';
    const iconBackColorOpen = isDarkMode ? theme.palette.grey[700] : theme.palette.grey[300];

    if (!user) {
        return '';
    }
    return (
        <Box sx={{ flexShrink: 0, ml: 0.75 }}>
            <ButtonBase
                sx={{
                    p: 0.25,
                    bgcolor: open ? iconBackColorOpen : 'transparent',
                    borderRadius: 1,
                    '&:hover': {
                        bgcolor: isDarkMode ? theme.palette.grey[600] : 'secondary.lighter',
                        '& .MuiTypography-root': {
                            color: 'text.primary'
                        }
                    }
                }}
                aria-label="open profile"
                ref={anchorRef}
                aria-controls={open ? 'profile-grow' : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
            >
                <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 0.5 }}>
                    <Avatar alt="profile user" sx={{ width: 32, height: 32 }}>
                        <PersonOutline />
                    </Avatar>

                    <Typography
                        variant="subtitle1"
                        sx={{
                            color: open ? 'text.primary' : 'inherit'
                        }}
                    >
                        {userEmail}
                    </Typography>
                </Stack>
            </ButtonBase>
            <Popper
                placement="bottom-end"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 9]
                            }
                        }
                    ]
                }}
            >
                {({ TransitionProps }) => (
                    <Transitions type="fade" in={open} {...TransitionProps}>
                        {open && (
                            <Paper
                                sx={{
                                    boxShadow: theme.customShadows.z1,
                                    width: 290,
                                    minWidth: 240,
                                    maxWidth: 290,
                                    [theme.breakpoints.down('md')]: {
                                        maxWidth: 250
                                    }
                                }}
                            >
                                <ClickAwayListener onClickAway={handleClose}>
                                    <MainCard elevation={0} border={false} content={false}>
                                        <CardContent sx={{ px: 2.5, pt: 3 }}>
                                            <Grid container justifyContent="space-between" alignItems="center">
                                                <Grid>
                                                    <Stack direction="row" spacing={1.25} alignItems="center">
                                                        <Avatar alt="profile user" sx={{ width: 32, height: 32 }}>
                                                            <PersonOutline />
                                                        </Avatar>
                                                        <Stack>
                                                            <Typography variant="h6">{userEmail}</Typography>
                                                            <Typography variant="body2" color="textSecondary">
                                                                {userGroup}
                                                            </Typography>
                                                        </Stack>
                                                    </Stack>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                        {open && (
                                            <>
                                                {/*
                                                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                                    <Tabs
                                                        variant="fullWidth"
                                                        value={value}
                                                        onChange={handleChange}
                                                        aria-label="profile tabs"
                                                    >
                                                        <Tab
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                textTransform: 'capitalize'
                                                            }}
                                                            icon={<UserOutlined style={{ marginBottom: 0, marginRight: '10px' }} />}
                                                            label="Profile"
                                                            {...a11yProps(0)}
                                                        />
                                                        <Tab
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                textTransform: 'capitalize'
                                                            }}
                                                            icon={<SettingOutlined style={{ marginBottom: 0, marginRight: '10px' }} />}
                                                            label="Setting"
                                                            {...a11yProps(1)}
                                                        />
                                                    </Tabs>
                                                </Box>
                                                        */}
                                                <Box value={value} index={0} dir={theme.direction}>
                                                    <ProfileTab />
                                                </Box>
                                                {/*<TabPanel value={value} index={1} dir={theme.direction}>
                                                    <SettingTab />
                                                </TabPanel>
                                                */}
                                            </>
                                        )}
                                    </MainCard>
                                </ClickAwayListener>
                            </Paper>
                        )}
                    </Transitions>
                )}
            </Popper>
        </Box>
    );
};

export default Profile;
